import { useState } from 'react'

import HistoryIcon from '@mui/icons-material/History'
import InfoIcon from '@mui/icons-material/Info'
import LaunchIcon from '@mui/icons-material/Launch'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import Avatar from '@mui/material/Avatar'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'

import Adres from '@components/Adres'
import { DisciplineIcon } from '@components/DisciplineIcon'
import { MyListButton } from '@components/MyListButton'
import Uitvoerende from '@components/Uitvoerende'
import { formatDate } from '@util/date-util'
import { createBadge } from '@util/kpi-util'

import type { Opdracht } from '../../../opdracht/types/Opdracht'
import { Link } from '../../../router/components/Link'
import { StyledIconLink } from '../../../theme/components/StyledIconLink'
import { OpdrachtStatus } from '../../components/OpdrachtStatus'

import { CombiOpdrachtKpi } from './CombiOpdrachtKpi'

const fromDetails = (opdracht, prop) =>
  opdracht && opdracht.details ? opdracht.details[prop] : ''
const fromCombi = (opdracht, prop) =>
  opdracht && opdracht.details && opdracht.details.combi
    ? opdracht.details.combi[prop] || ''
    : ''

function DirtyIcon() {
  return (
    <span style={{ paddingLeft: 7 }}>
      <Tooltip title="Door wijzigingen worden KPI's herberekend">
        <HistoryIcon />
      </Tooltip>
    </span>
  )
}

function StatusIcon({ opdracht }) {
  const kpi = fromCombi(opdracht, 'kpi')
  const badge = kpi && createBadge(kpi)

  if (fromCombi(opdracht, 'isDirty')) return <DirtyIcon />

  return badge ? <CombiOpdrachtKpi kpi={kpi} /> : null
}
const STATUS_OPLEVERING = 500

interface Props {
  opdracht: Opdracht
  showProjectOptions?: boolean
}

export const CombiOpdrachtenTableRow = ({
  opdracht,
  showProjectOptions = false,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  const handleMenu = (event) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (event) => {
    event.stopPropagation()
    setAnchorEl(null)
  }

  return (
    <>
      <TableRow key={opdracht.opdrachtId}>
        <TableCell>
          <StatusIcon opdracht={opdracht} />
        </TableCell>
        <TableCell>{opdracht.opdrachtId}</TableCell>
        <TableCell>
          {fromDetails(opdracht, 'clusterCodeNB') ? (
            <Tooltip
              title={fromDetails(opdracht, 'clusterCodeNB')}
              placement="right"
            >
              <span>{opdracht.clusterId}</span>
            </Tooltip>
          ) : (
            opdracht.clusterId
          )}
        </TableCell>
        <TableCell>
          <Chip
            variant="outlined"
            avatar={
              <Tooltip
                title={fromCombi(opdracht, 'realisatietype')}
                placement="right"
              >
                <Avatar
                  sx={{
                    color: 'black !important',
                    cursor: 'default',
                    backgroundColor: '#eeeeee',
                  }}
                >
                  {fromCombi(opdracht, 'realisatietype').substring(0, 1)}
                </Avatar>
              </Tooltip>
            }
            label={formatDate(fromCombi(opdracht, 'realisatiedatum'))}
          />
        </TableCell>
        <TableCell>
          <DisciplineIcon
            disciplineCode={opdracht.discipline}
            toolTip={opdracht.disciplineLabel}
          />
        </TableCell>
        <TableCell>
          {opdracht.details && <Adres {...opdracht.details} />}
        </TableCell>
        <TableCell>{opdracht.netbeheerder}</TableCell>
        <TableCell>
          <Uitvoerende {...opdracht} />
        </TableCell>
        <TableCell>{fromCombi(opdracht, 'gebiedscodeLabel')}</TableCell>
        <TableCell>
          {opdracht.status === STATUS_OPLEVERING ? (
            <OpdrachtStatus opdracht={opdracht} text={opdracht.statusText} />
          ) : (
            <p>{opdracht.statusText}</p>
          )}
        </TableCell>
        <TableCell>{formatDate(opdracht.changed)}</TableCell>
        <TableCell>
          <MyListButton
            type="hoofdleiding"
            disabled={true}
            active={opdracht.myList}
            aanvraagID={''}
          />

          <StyledIconLink
            title={`Navigeer naar opdracht ${opdracht.opdrachtId}`}
            icon={<LaunchIcon />}
            href={`/combiopdracht/${opdracht.opdrachtId}`}
          />
          <IconButton data-testid="buttonMenu" onClick={handleMenu}>
            <MoreVertIcon />
          </IconButton>
          {showProjectOptions && ( // this check is outside as the project details is now the only button there
            <Menu
              id="menu-appbar"
              data-testid="menu"
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              keepMounted
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={open}
              onClose={handleClose}
            >
              <Link
                to={`/aansluiting/project/${opdracht.clusterId}`}
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <MenuItem>
                  <ListItemIcon>
                    <InfoIcon />
                  </ListItemIcon>
                  <ListItemText primary="Project details" />
                </MenuItem>
              </Link>
            </Menu>
          )}
        </TableCell>
      </TableRow>
    </>
  )
}
